import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Reusable skeleton loader component for card loading states.
 *
 * Displays animated shimmer placeholders that match card content dimensions
 * to prevent layout shift during data loading.
 *
 * Uses the loadingGradient mixin from _v2.scss for consistent animation.
 *
 * @example
 * <app-card-skeleton-loader [lineCount]="5"></app-card-skeleton-loader>
 */
@Component({
    selector: 'app-card-skeleton-loader',
    templateUrl: './card-skeleton-loader.component.html',
    styleUrls: ['./card-skeleton-loader.component.scss'],
    standalone: true,
    imports: [CommonModule],
})
export class CardSkeletonLoaderComponent {
    /**
     * Number of skeleton lines to display.
     * Each line represents a typical data row in the card.
     */
    @Input() lineCount: number = 5;

    /**
     * Whether to show a skeleton header bar.
     * Useful for cards with prominent headers.
     */
    @Input() showHeader: boolean = false;

    /**
     * Height of each skeleton line in pixels.
     * Default matches typical line-item height with margin.
     */
    @Input() lineHeight: number = 32;

    /**
     * Generates an array for *ngFor iteration based on lineCount.
     */
    get lines(): number[] {
        return Array(this.lineCount).fill(0).map((_, i) => i);
    }

    /**
     * Get randomized width percentage for skeleton line.
     * Creates more realistic loading appearance by varying line widths.
     *
     * @param index Line index (0-based)
     * @returns Width percentage (60-100%)
     */
    getLineWidth(index: number): number {
        // Use index as seed for consistent but varied widths
        const widths = [85, 95, 75, 90, 100, 80, 92, 88];
        return widths[index % widths.length];
    }
}
